import { useEffect, useState, useCallback } from "react";
import { Outlet } from "react-router-dom";
import PropTypes from "prop-types";

import { useSessionStore } from "../../store/session-store.js";
import { useAxiosPrivate } from "../../hooks/useAxiosPrivate.js";
import { getUrl } from "./helper.js";
import { PRODUCT_NAMES } from "../llm-whisperer/helper.js";
import { GenericLoader } from "../../components/generic-loader/GenericLoader.jsx";
import { useExceptionHandler } from "../../hooks/useExceptionHandler.jsx";
import { useAlertStore } from "../../store/alert-store.js";
import { useGoogleTagManager } from "../hooks/useGoogleTagManager.js";
import { GetStarted } from "./GetStarted.jsx";

function OnboardProduct({ type }) {
  const [isUserOnboarded, setIsUserOnboarded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscriptionApiLoading, setIsSubscriptionApiLoading] =
    useState(false);

  const { sessionDetails } = useSessionStore();
  const axiosPrivate = useAxiosPrivate();
  const { setAlertDetails } = useAlertStore();
  const handleException = useExceptionHandler();
  const handleGoogleTagManager = useGoogleTagManager();

  useEffect(() => {
    if (!type) return;

    const fetchUserOnboardStatus = async () => {
      setIsLoading(true);

      const requestOptions = {
        method: "GET",
        url: getUrl(type, sessionDetails?.orgId),
      };

      try {
        const res = await axiosPrivate(requestOptions);
        const data = res?.data;
        setIsUserOnboarded(data?.subscription);
      } catch (err) {
        setAlertDetails(handleException(err));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserOnboardStatus();
  }, [type]);

  const handleGetStarted = useCallback(async () => {
    let method = "POST";
    if (type === PRODUCT_NAMES.llmWhisperer) {
      method = "PUT";
    }

    const requestOptions = {
      method,
      url: getUrl(type, sessionDetails?.orgId),
      headers: {
        "X-CSRFToken": sessionDetails?.csrfToken,
      },
    };

    setIsSubscriptionApiLoading(true);

    try {
      await axiosPrivate(requestOptions);
      setIsUserOnboarded(true);
      handleGoogleTagManager(type);
    } catch (err) {
      setIsUserOnboarded(false);
      setAlertDetails(handleException(err));
    } finally {
      setIsSubscriptionApiLoading(false);
    }
  }, [type]);

  if (!type || !sessionDetails?.isLoggedIn) {
    return <Outlet />;
  }

  if (isLoading) {
    return <GenericLoader />;
  }

  if (!isUserOnboarded) {
    return (
      <GetStarted
        type={type}
        handleGetStarted={handleGetStarted}
        isLoading={isSubscriptionApiLoading}
      />
    );
  }

  return <Outlet />;
}

OnboardProduct.propTypes = {
  type: PropTypes.string.isRequired,
};

export { OnboardProduct };
